import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { MeshPhysicalMaterialParameters } from "three";
import * as THREE from "three";
import type { PanelColor } from "../interfaces/PanelColor";
import { fieldToTintMap, heightMapToRoughnessMap } from "./proceduralMaps";
import { tileableFbm, tileableFbm2 } from "./proceduralNoise";

/**
 * Surface for coated panels — phenolic-faced birch multiplex (the smooth
 * "film" face, not the wire-mesh one), and the honeycomb and Poly-Board
 * boards finished the same way.
 *
 * What the smooth face of a new board shows, from samples and from case
 * manufacturers' product photography:
 *
 * - A dark, satin-gloss dielectric. The reflection of a light is a broad soft
 *   streak with a clear edge — neither a mirror image nor a matte glow. The
 *   film is cured phenolic resin, IOR about 1.6: a little more head-on
 *   reflection than the generic 1.5 dielectric, and on a surface this dark
 *   the reflection is most of what one sees.
 * - The film is a *coloured translucent* paper, hot-pressed on. The veneer's
 *   grain shows through it faintly — the film suppliers say so outright,
 *   "less visible in the darker colours" — as fine, straight, low-contrast
 *   fibre lines along the board, a few percent of tone. Birch: no cathedral
 *   figure, nothing bold. On a case, the striations are just visible on the
 *   large panels where the light rakes across them.
 * - The gloss is not perfectly even: the press leaves a faint haze of
 *   lengthwise streaks in the highlight. Roughness, not shape.
 * - The board is flat and the film has no texture of its own. No normal map.
 *   And a case is sold new: no scratches, no scuffs, nothing that says
 *   "used". Wear is not part of this material.
 *
 * Both maps are baked from tileable fields once per (size, strength, gloss)
 * and shared between panels; each panel gets clones so it can carry its own
 * `repeat`.
 */
interface SurfaceMaps {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
}

const textureCache = new Map<string, SurfaceMaps>();
const TEXTURE_CACHE_LIMIT = 8;

/** Physical size of one tile, in millimetres. */
const TILE_SIZE_MM = 400;

/**
 * Bake resolution. The finest thing in either map is a fibre line a couple of
 * millimetres apart, which 256 texels over 400 mm resolves; the bake is
 * synchronous main-thread work, and mipmaps and anisotropy carry the rest.
 */
const TEXTURE_SIZE = 256;

/**
 * Lattice cells along and across the grain for the fibre lines.
 *
 * Wood is strongly directional: fibres run the length of the board, so very
 * few cells along and many across. Along U, which is the panel's width; at
 * the tile above the fibre spacing lands near 8 mm.
 */
const FIBRE_PERIOD_ALONG = 2;
const FIBRE_PERIOD_ACROSS = 48;
const FIBRE_OCTAVES = 3;

/**
 * How far the grain may darken the panel colour, at full strength. A few
 * percent: the film is near-opaque, and on the dark browns and blacks flight
 * cases are built in the grain is a hint under the gloss, not a wood finish.
 */
const GRAIN_TINT_LOW = 0.94;
const GRAIN_TINT_HIGH = 1;

/**
 * Peak-to-peak roughness of the press haze around the base gloss. Enough to
 * give the highlight a faint brushed grain; not enough to read as a texture
 * on its own.
 */
const HAZE_ROUGHNESS_SPAN = 0.06;
const HAZE_PERIOD_ALONG = 1;
const HAZE_PERIOD_ACROSS = 64;
const HAZE_OCTAVES = 3;

/**
 * Phenolic resin. 1.5 is three's default and glass; cured phenolics sit at
 * about 1.6, which lifts the head-on reflectance from 4% to 5.3%.
 */
const PHENOLIC_IOR = 1.6;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** Tone of the face: straight fibre lines with a slow, gentle blotch. In [0, 1]. */
export function generateGrainField(size: number): Float32Array {
  const field = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = x / size;
      const v = y / size;
      const fibres = tileableFbm2(
        u,
        v,
        FIBRE_PERIOD_ALONG,
        FIBRE_PERIOD_ACROSS,
        FIBRE_OCTAVES,
      );
      const blotch = tileableFbm(u, v, 3, 2);
      field[y * size + x] = fibres * 0.75 + blotch * 0.25;
    }
  }
  return field;
}

/** Absolute roughness per texel: the base gloss, hazed along the press direction. */
export function generateRoughnessField(
  size: number,
  baseRoughness: number,
): Float32Array {
  const field = new Float32Array(size * size);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const haze = tileableFbm2(
        x / size,
        y / size,
        HAZE_PERIOD_ALONG,
        HAZE_PERIOD_ACROSS,
        HAZE_OCTAVES,
      );
      field[y * size + x] = clamp01(
        baseRoughness + (haze - 0.5) * HAZE_ROUGHNESS_SPAN,
      );
    }
  }
  return field;
}

/**
 * Bakes (or fetches) the two maps for a given gloss and grain strength.
 * Hands out clones; see the note on `useEffect` below.
 */
export function getCoatedPanelSurfaceMaps(
  size: number,
  strength: number,
  baseRoughness: number,
): SurfaceMaps {
  const key = `${size}-${strength.toFixed(2)}-${baseRoughness.toFixed(3)}`;
  const cached = textureCache.get(key);
  if (cached) {
    textureCache.delete(key);
    textureCache.set(key, cached);
    return cloneSurfaceMaps(cached);
  }

  const roughness = generateRoughnessField(size, baseRoughness);
  let lowest = Number.POSITIVE_INFINITY;
  let highest = Number.NEGATIVE_INFINITY;
  for (const value of roughness) {
    if (value < lowest) lowest = value;
    if (value > highest) highest = value;
  }
  const maps: SurfaceMaps = {
    map: fieldToTintMap(
      size,
      generateGrainField(size),
      1 - (1 - GRAIN_TINT_LOW) * strength,
      GRAIN_TINT_HIGH,
    ),
    // Absolute values, so the map's own extremes are the ramp — the
    // renormalisation in `heightMapToRoughnessMap` becomes the identity.
    roughnessMap: heightMapToRoughnessMap(size, roughness, lowest, highest),
  };

  if (textureCache.size >= TEXTURE_CACHE_LIMIT) {
    const oldest = textureCache.keys().next().value;
    const evicted = oldest === undefined ? undefined : textureCache.get(oldest);
    if (oldest !== undefined) textureCache.delete(oldest);
    if (evicted) disposeSurfaceMaps(evicted);
  }
  textureCache.set(key, maps);
  return cloneSurfaceMaps(maps);
}

/**
 * Clones a texture without invalidating the pixels its siblings already have on
 * the GPU.
 *
 * A clone shares the base texture's `Source` - that is what uploads these pixels
 * exactly once for every panel using them. But `Texture.copy` ends with
 * `needsUpdate = true`, and that setter bumps `source.version` as well as the
 * clone's own, so `WebGLTextures.uploadTexture` then re-runs `texImage2D` and
 * `generateMipmap` for every texture on that source. A resize drag re-clones per
 * panel per size step, so the cost is N panels x 2 maps of byte-identical pixels
 * per tick. The clone only needs its own `repeat`, so put the source version back
 * where it was; see `coatedPanelTextureShare.test.ts`.
 */
function cloneSharingSource<T extends THREE.Texture>(texture: T): T {
  const uploadedVersion = texture.source.version;
  const clone = texture.clone() as T;
  texture.source.version = uploadedVersion;
  return clone;
}

function cloneSurfaceMaps(maps: SurfaceMaps): SurfaceMaps {
  return {
    map: cloneSharingSource(maps.map),
    roughnessMap: cloneSharingSource(maps.roughnessMap),
  };
}

function disposeSurfaceMaps(maps: SurfaceMaps): void {
  maps.map.dispose();
  maps.roughnessMap.dispose();
}

interface CoatedPanelMaterialProps {
  color: PanelColor;
  /** Panel size for texture scaling - ensures consistent texture scale regardless of cutouts */
  panelSize?: THREE.Vector2;
  /** Physical size of one tile, in millimetres. */
  tileSizeMm?: number;
  /** Gloss of the film; the press haze varies around it. */
  roughness?: number;
  /** Base metalness (0-1) */
  metalness?: number;
  /** How strongly the grain shows through the film, 0-1. */
  grainStrength?: number;
  /**
   * Texture resolution (power of 2 recommended). Kept modest because the bake
   * is synchronous main-thread work; mipmaps and anisotropy carry the quality.
   */
  textureSize?: number;
}

export const CoatedPanelMaterial = ({
  color,
  panelSize,
  tileSizeMm = TILE_SIZE_MM,
  roughness = 0.3,
  metalness: explicitMetalness,
  grainStrength = 1,
  textureSize = TEXTURE_SIZE,
}: CoatedPanelMaterialProps) => {
  const threeColor = useMemo(() => new THREE.Color(color.color), [color.color]);
  // Coated board is a dielectric — see PANEL_METALNESS in PanelMaterial.
  const metalness = explicitMetalness ?? 0;

  // Only the two components are read, and a rebuilt part definition hands us a
  // fresh Vector2 with the same numbers on every drag frame — keying the memo
  // on the object would re-clone the textures each time.
  const panelWidth = panelSize?.x;
  const panelHeight = panelSize?.y;

  const maps = useMemo(() => {
    const surface = getCoatedPanelSurfaceMaps(
      textureSize,
      grainStrength,
      roughness,
    );

    // Repeat by physical size, so the grain stays the same width on a small
    // tray panel and on a full lid, and does not stretch around cutouts.
    if (panelWidth !== undefined && panelHeight !== undefined) {
      for (const map of [surface.map, surface.roughnessMap]) {
        map.repeat.set(panelWidth / tileSizeMm, panelHeight / tileSizeMm);
      }
    }

    return surface;
  }, [
    textureSize,
    grainStrength,
    roughness,
    panelWidth,
    panelHeight,
    tileSizeMm,
  ]);

  // `getCoatedPanelSurfaceMaps` hands out clones so each panel can carry its
  // own `repeat`. They share the cached source, but each one still holds a
  // reference on it and re-uploads on first use, so the clones have to be
  // released when this panel stops using them.
  useEffect(() => () => disposeSurfaceMaps(maps), [maps]);

  // The roughness map is not a JSX prop on purpose. @react-three/fiber v8
  // tags every 8-bit RGBA texture it is handed as a prop with the renderer's
  // output colour space (sRGB), whatever the slot; v9 does so for colour maps
  // only. Decoded as sRGB, a roughness of 0.3 reads as about 0.07 and the
  // panels turn into mirrors. Assigned here, the map keeps the colour space it
  // was baked with under either version. A layout effect puts it in place
  // before the first frame compiles the material.
  const materialRef = useRef<THREE.MeshPhysicalMaterial>(null);
  useLayoutEffect(() => {
    const material = materialRef.current;
    if (!material) return;
    // The shader define follows the map's presence, not its identity: a swap
    // of the clone (new size, new gloss) needs no recompile.
    const hadMap = material.roughnessMap !== null;
    material.roughnessMap = maps.roughnessMap;
    if (!hadMap) material.needsUpdate = true;
  }, [maps.roughnessMap]);

  const materialProps: MeshPhysicalMaterialParameters = {
    color: threeColor,
    map: maps.map,
    // The map carries absolute roughness; three multiplies the two.
    roughness: 1,
    metalness,
    ior: PHENOLIC_IOR,
  };

  return <meshPhysicalMaterial ref={materialRef} {...materialProps} />;
};
